import { forwardRef, type ReactNode, type ElementType } from 'react';
import * as React from 'react';
import clsx from 'clsx';
import styles from './Paragraph.module.css';

type SpanProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLSpanElement>,
  HTMLSpanElement
>;

export type ParagraphVariant =
  | 'body'
  | 'lead'
  | 'caption'
  | 'caption-xs'
  | 'h7';

export interface Props<T extends ElementType>
  extends Pick<SpanProps, 'className'> {
  variant?: ParagraphVariant;
  children: ReactNode;
  testid?: string;
  as?: T; // `as="div"` renders a `<div />` tag instead of `<p />` to avoid semantic errors
}

const getVariantClass = (variant?: ParagraphVariant) => {
  switch (variant) {
    case 'h7':
      return styles.h7;
    case 'lead':
      return styles.lead;
    case 'caption':
      return styles.caption;
    case 'caption-xs':
      return styles['caption-xs'];
  }
  return styles['body'];
};

export const Paragraph = forwardRef(
  <T extends React.ElementType>(
    props: Props<T>,
    ref: React.ComponentProps<T>['ref']
  ) => {
    const { variant, testid, className, as: As = 'p' } = props;
    const classNames = clsx(getVariantClass(variant), className);

    return (
      <As
        ref={ref}
        data-testid={testid}
        className={classNames}
        role="paragraph"
      >
        {props.children}
      </As>
    );
  }
) as <T extends React.ElementType>(
  props: Props<T> & Omit<React.ComponentProps<T>, keyof Props<T>>
) => ReactNode;
